import { useState } from 'react'
import Header from '@/components/Header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Trash2, Plus, Edit, Loader2, MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useMembershipTemplates } from '@/hooks/use-membership-templates'
import {
  MembershipTemplate,
  MembershipTemplateFormData
} from '@/constants'

const getDefaultFormData = (): MembershipTemplateFormData => ({
  name: '',
  durationMonths: 1,
  priceCouples: 0,
  priceSingleLadies: 0,
  priceSingleMales: 0,
  isActive: true
})

const MembershipTemplatesPage: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<MembershipTemplate | null>(null)
  const [formData, setFormData] = useState<MembershipTemplateFormData>(getDefaultFormData())

  const {
    templates,
    isLoading,
    isSubmitting,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleActive
  } = useMembershipTemplates()

  const resetForm = () => {
    setFormData(getDefaultFormData())
  }

  const handleCreate = () => {
    resetForm()
    setIsCreateDialogOpen(true)
  }

  const handleEdit = (template: MembershipTemplate) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      durationMonths: template.durationMonths,
      priceCouples: parseFloat(template.priceCouples) || 0,
      priceSingleLadies: parseFloat(template.priceSingleLadies) || 0,
      priceSingleMales: parseFloat(template.priceSingleMales) || 0,
      isActive: template.isActive
    })
    setIsEditDialogOpen(true)
  }

  const handleSubmit = async (isEdit: boolean = false) => {
    if (!formData.name || formData.durationMonths < 1) {
      return
    }

    let success: boolean
    if (isEdit && editingTemplate) {
      success = await updateTemplate(editingTemplate.id, formData)
    } else {
      success = await createTemplate(formData)
    }

    if (success) {
      setIsCreateDialogOpen(false)
      setIsEditDialogOpen(false)
      setEditingTemplate(null)
      resetForm()
    }
  }

  const handleDelete = async (template: MembershipTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return
    }
    await deleteTemplate(template.id)
  }

  const handleToggleActive = async (template: MembershipTemplate) => {
    await toggleActive(template.id, !template.isActive)
  }

  const formatPrice = (price: string | number) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    return numPrice === 0 ? 'FREE' : `$${numPrice.toFixed(2)}`
  }

  const formatDuration = (months: number) => {
    if (months === 1) return '1 Month'
    if (months < 12) return `${months} Months`
    const years = months / 12
    if (years === 1) return '1 Year'
    return `${years} Years`
  }

  const renderTemplateForm = (isEdit: boolean) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="name">Template Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., 1 Month"
        />
      </div>

      <div>
        <Label htmlFor="durationMonths">Duration (Months) *</Label>
        <Input
          id="durationMonths"
          type="number"
          min="1"
          value={formData.durationMonths}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            durationMonths: parseInt(e.target.value) || 1
          }))}
        />
      </div>

      <div className="space-y-3">
        <Label>Pricing (USD)</Label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="priceCouples" className="text-xs text-muted-foreground">Couples</Label>
            <Input
              id="priceCouples"
              type="number"
              min="0"
              step="0.01"
              value={formData.priceCouples}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                priceCouples: parseFloat(e.target.value) || 0
              }))}
            />
          </div>
          <div>
            <Label htmlFor="priceSingleLadies" className="text-xs text-muted-foreground">Single Ladies</Label>
            <Input
              id="priceSingleLadies"
              type="number"
              min="0"
              step="0.01"
              value={formData.priceSingleLadies}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                priceSingleLadies: parseFloat(e.target.value) || 0
              }))}
            />
          </div>
          <div>
            <Label htmlFor="priceSingleMales" className="text-xs text-muted-foreground">Single Males</Label>
            <Input
              id="priceSingleMales"
              type="number"
              min="0"
              step="0.01"
              value={formData.priceSingleMales}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                priceSingleMales: parseFloat(e.target.value) || 0
              }))}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
        />
        <Label htmlFor="isActive">Active</Label>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setIsCreateDialogOpen(false)
            setIsEditDialogOpen(false)
            setEditingTemplate(null)
            resetForm()
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit(isEdit)}
          disabled={isSubmitting || !formData.name || formData.durationMonths < 1}
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      <Header />
      <div className="max-w-5xl mx-auto px-5 pb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Membership Templates</h1>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Membership Template</DialogTitle>
              </DialogHeader>
              {renderTemplateForm(false)}
            </DialogContent>
          </Dialog>
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Templates</h2>

          {error ? (
            <div className="text-destructive">Failed to load templates</div>
          ) : isLoading ? (
            <div className="text-muted-foreground">Loading templates...</div>
          ) : !templates.length ? (
            <div className="text-muted-foreground">No templates configured. Click "Add Template" to create one.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Couples</TableHead>
                    <TableHead>Single Ladies</TableHead>
                    <TableHead>Single Males</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>{formatDuration(template.durationMonths)}</TableCell>
                      <TableCell>{formatPrice(template.priceCouples)}</TableCell>
                      <TableCell>{formatPrice(template.priceSingleLadies)}</TableCell>
                      <TableCell>{formatPrice(template.priceSingleMales)}</TableCell>
                      <TableCell>
                        <Switch
                          checked={template.isActive}
                          onCheckedChange={() => handleToggleActive(template)}
                        />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleEdit(template)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(template)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Membership Template</DialogTitle>
            </DialogHeader>
            {renderTemplateForm(true)}
          </DialogContent>
        </Dialog>
      </div>
    </>
  )
}

export default MembershipTemplatesPage
